import { ActionModal } from '@kcftech/design-system';
import {
    IPhysicalSensorModelSdResponse,
    PhysicalSensorModelUpdateRequest,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { NotesMaxLength, notesRules } from 'pages/Settings/ThirdPartySensorModels/shared';
import { StyledTextArea } from 'pages/Settings/ThirdPartySensorModels/styled';
import { useForm } from 'react-hook-form';

type EditThirdPartySensorForm = Readonly<{
    notes: string;
}>;

export type EditThirdPartySensorModalProps = Readonly<{
    physicalSensorModel: IPhysicalSensorModelSdResponse;
    onClose: (updated?: boolean) => void;
}>;

export function EditThirdPartySensorModal({
    physicalSensorModel,
    onClose,
}: EditThirdPartySensorModalProps) {
    const { showToast } = useNotificationContext();

    const {
        register,
        handleSubmit,
        formState: { isSubmitted, isValid, isValidating, isSubmitting, errors },
    } = useForm<EditThirdPartySensorForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            notes: physicalSensorModel.Notes,
        },
    });

    const { mutate: editThirdPartySensor, isPending: isSaving } = useMutation({
        mutationFn: async (data: EditThirdPartySensorForm) =>
            sdClient.updatePhysicalSensorModel({
                physicalSensorModelId: physicalSensorModel.Id,
                body: new PhysicalSensorModelUpdateRequest({
                    Notes: data.notes,
                }),
            }),

        onSuccess: () => {
            showToast({
                type: 'success',
                content: 'Successfully updated third party sensor model',
            });
            onClose(true);
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to update third party sensor model',
            }),
    });

    const onSave = (data: EditThirdPartySensorForm) => {
        editThirdPartySensor(data);
    };

    return (
        <ActionModal
            height={425}
            title="Edit Third-Party Sensor"
            actionButtonText="Save"
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting) && !isSaving
            }
            onAction={handleSubmit(onSave)}
            onClose={onClose}
        >
            <ValidatableLabel label="Notes" error={errors.notes}>
                <StyledTextArea
                    minRows={8}
                    className="mt-5"
                    placeholder="Please provide more detail for context"
                    maxLength={NotesMaxLength}
                    showLengthIndicator
                    $lightBackground
                    {...register('notes', notesRules)}
                />
            </ValidatableLabel>
        </ActionModal>
    );
}
